import React, { useContext, useEffect, useState } from 'react';
import { BrcContext } from '../BrcContextWrapper';
import { deleteQuestion, getAnswerByQuestion, getQuestionByCampaign } from '../service/brc';

import DataTable from '../components/datatable';

import { Accordion, Badge, Button, Card, Col, Form, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { FaArrowRight, FaCircle, FaPencilAlt, FaQuestionCircle, FaTrash } from 'react-icons/fa';
import { useLocation, useNavigate } from 'react-router-dom';
import PopUpModal from '../components/PopUpModal';
import { AlertDismissable } from '../components/Alerts';

import QuestionForm from '../components/QuestionForm';

const formatNumber = (n) => (n != null ? n.toLocaleString() : '-');
const formatCurrency = (n) => (n != null ? `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-');
const formatPercent = (n) => (n != null ? `${n.toFixed(2)}%` : '-');
const formatRate = (n) => (n != null ? `${(n * 100).toFixed(2)}%` : '-');

const columnHeader = (label, tooltip) => (
  <OverlayTrigger placement="top" overlay={<Tooltip>{tooltip}</Tooltip>}>
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3em', cursor: 'help' }}>
      {label} <FaQuestionCircle size={11} style={{ opacity: 0.5 }} />
    </span>
  </OverlayTrigger>
);

const Question = () => {

  const { show, toggleShow, deleteShow, toggleDelete, isEdit, toggleIsEdit, sessionUser, questionColumns, setQuestionColumns } = useContext(BrcContext)
  const [questions, setQuestions] = useState([])
  const [campaignSummary, setCampaignSummary] = useState(null)
  const { state } = useLocation()

  const [pending, setPending] = useState(true)

  const navigate = useNavigate()

  const [currentQuestionToDelete, setcurrentQuestionToDelete] = useState()
  const [editError, setEditError] = useState('')
  const handleClose = () => toggleDelete()
  const handleShow = e => {
    toggleShow()
  }
  const [selectedRow, setSelectedRow] = useState('')


  const loadQuestions = () => {
    const campaignId = state.campaignId || state.id;
    getQuestionByCampaign(campaignId).then(res => {
      const responseData = res.data._data;
      const questionList = Array.isArray(responseData) ? responseData : (responseData?.questions || []);
      setQuestions(questionList.reverse());
      const summary = res.data.campaignSummary ?? responseData?.campaignSummary ?? null;
      setCampaignSummary(summary);
      setPending(false);
    }).catch(() => {
      setQuestions([]);
      setPending(false);
    });
  }

  useEffect(() => {
    if (state == undefined || state == null || state == '' || !sessionUser) {
      navigate('/')
    } else {
     loadQuestions();
    }
  }, [])

  const customStyles = {
    headCells: {
      style: {
        float: 'right ',
        backgroundColor: ' #cccccc',
        fontWeight: 'bold',
        fontSize: '13px'
      }
    }
  }

  const liftErrorCell = (row) => (
    <OverlayTrigger
      placement="top"
      overlay={<Tooltip>{row.brandLiftCalculationError}</Tooltip>}
    >
      <span style={{ display: 'flex', gap: '0.5em', alignItems: 'center' }}>
        N/A
        <FaQuestionCircle />
      </span>
    </OverlayTrigger>
  );

  let columns = [
      {
        name: 'Status',
        selector: row =>
          row.isActive ? <FaCircle style={{ color: 'green' }} /> : <FaCircle style={{ color: 'red' }} />,
        center: true,
        width: '70px',
      },
      {
        name: 'Question',
        selector: row => (
          <span style={{ display: 'flex', gap: '0.5em', alignItems: 'center', flexWrap: 'wrap' }}>
            {row.question}
            {row.hasPreferredAnswer === false && (
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip>Mark one of the answers as preferred — without it this question has no Brand Lift and no statistical significance</Tooltip>}
              >
                <Badge bg="warning" text="dark">No preferred answer</Badge>
              </OverlayTrigger>
            )}
          </span>
        ),
        grow: 2,
      },
      {
        name: 'Brand Metric Type',
        id: 'brandMetricType',
        label: 'Brand Metric Type',
        selector: row => row.brandMetricType,
        center: true,
        showInColumnFilters: true,
      },
      {
        name: 'Multiple Answers',
        id: 'multipleAnswers',
        label: 'Multiple Answers',
        selector: row => (row.isMultiple ? 'Yes' : 'No'),
        center: true,
        showInColumnFilters: true,
      },
      {
        name: 'Responses/Views',
        id: 'responsesViews',
        label: 'Responses/Views',
        selector: row => `${row.submitCount ?? 0} / ${row.viewCount ?? 0}`,
        center: true,
        showInColumnFilters: true,
      },
      {
        name: 'Responses/Views (Control)',
        id: 'responsesViewsControl',
        label: 'Responses/Views (Control)',
        selector: row => `${row.controlSubmit ?? 0} / ${row.controlView ?? 0}`,
        center: true,
        showInColumnFilters: true,
      },
      {
        name: 'Responses/Views (Exposed)',
        id: 'responsesViewsExposed',
        label: 'Responses/Views (Exposed)',
        selector: row => `${row.exposedSubmit ?? 0} / ${row.exposedView ?? 0}`,
        center: true,
        showInColumnFilters: true,
      },
      {
        name: columnHeader('Pref. Rate (Exposed)', 'Percentage of exposed users who chose the preferred answer'),
        id: 'prefRateExposed',
        label: 'Pref. Rate (Exposed)',
        selector: (row) => row.brandLiftCalculationError
          ? liftErrorCell(row)
          : formatPercent(row.preferredAnswerRateExposed),
        center: true,
        showInColumnFilters: true,
      },
      {
        name: columnHeader('Pref. Rate (Control)', 'Percentage of control users who chose the preferred answer'),
        id: 'prefRateControl',
        label: 'Pref. Rate (Control)',
        selector: (row) => row.brandLiftCalculationError
          ? '-'
          : formatPercent(row.preferredAnswerRateControl),
        center: true,
        showInColumnFilters: true,
      },
      {
        name: columnHeader('Absolute Lift', 'Difference in preferred answer rates between exposed and control groups (in percentage points)'),
        id: 'absoluteLift',
        label: 'Absolute Lift',
        selector: (row) => row.brandLiftCalculationError
          ? '-'
          : `${row.absoluteLift >= 0 ? '+' : ''}${row.absoluteLift?.toFixed(2)}pp`,
        center: true,
        showInColumnFilters: true,
      },
      {
        name: columnHeader('Relative Lift', 'Percentage change in preferred answer rate of exposed vs. control: (exposed / control − 1) × 100'),
        id: 'relativeLift',
        label: 'Relative Lift',
        selector: (row) => {
          if (row.brandLiftCalculationError) return '-';
          if (row.brandLift == null) return '-';
          return `${row.brandLift >= 0 ? '+' : ''}${row.brandLift.toFixed(2)}%`;
        },
        center: true,
        showInColumnFilters: true,
      },
      {
        name: columnHeader('Significance', 'Statistical significance via Two-Proportion Z-Test (90%, 95% or 99% confidence level)'),
        id: 'significance',
        label: 'Significance',
        selector: (row) => {
          if (row.brandLiftCalculationError) return '-';
          const sig = row.statisticalSignificance;
          if (!sig) return '-';
          if (sig.isSignificant) {
            if (!Number.isFinite(sig.confidenceLevel)) return '-';
            return <Badge bg="success">{`${Math.round(sig.confidenceLevel * 100)}%`}</Badge>;
          }
          return <Badge bg="secondary">Not sig.</Badge>;
        },
        center: true,
        showInColumnFilters: true,
      },
      {
        name: 'Actions',
        selector: row => (
          <div>
            {sessionUser && sessionUser.isAdmin && (
              <OverlayTrigger placement='top' overlay={<Tooltip>Edit</Tooltip>}>
                <Button variant='primary' size='sm' onClick={() => editHandler(row)}>
                  <FaPencilAlt />
                </Button>
              </OverlayTrigger>
            )}{' '}
            {sessionUser && sessionUser.isAdmin && (
              <OverlayTrigger placement='top' overlay={<Tooltip>Delete</Tooltip>}>
                <Button variant='danger' size='sm' onClick={() => deleteHandler(row)}>
                  <FaTrash />
                </Button>
              </OverlayTrigger>
            )}{' '}
            <OverlayTrigger placement='top' overlay={<Tooltip>See Answers</Tooltip>}>
              <Button variant='info' size='sm' onClick={() => detailHandler(row)}>
                <FaArrowRight />
              </Button>
            </OverlayTrigger>
          </div>
        ),
        width: '180px',
        center: true
      }
    ].map((col) => col.id ? { ...col, omit: !questionColumns.includes(col.id) } : col),
    data = questions.map((item) => ({
      id: item._id,
      campaignName: state?.campaignName ?? '',
      ...item,
    }));

  const deleteHandler = state => {
      setcurrentQuestionToDelete(state.id)
      toggleDelete()
    },
    deleteObject = () => {
      let reqData = {
        email: sessionUser.email,
      }
      deleteQuestion(reqData, currentQuestionToDelete).then(() => {
        toggleDelete()
        loadQuestions();
      })
    },
    editHandler = async state => {
      const response = await getAnswerByQuestion(state.id)
      if (response?.data?._status !== true) {
        setEditError(response?.data?._message ?? 'Failed to load answers for this question.')
        return
      }

      const answers = response.data._data.map(answer => ({
        _id: answer._id,
        answer: answer.answer,
        isCorrect: answer.isCorrect === true
      }))

      setSelectedRow({
        id: state.id,
        campaignId: state.campaignId,
        question: state.question,
        isMultiple: state.isMultiple,
        isActive: state.isActive,
        brandMetricType: state.brandMetricType,
        answers
      })
      toggleIsEdit(true)
      handleShow()
    },
    createHandler = () => {
      if (data.length <= 2) {
        setSelectedRow({
          campaignId: state.campaignId ? state.campaignId : state.id,
          question: '',
          isMultiple: false,
          isActive: true,
          brandMetricType: ''
        })
        toggleIsEdit(false)
        handleShow()
      } else {
        alert('You can create only 3 questions for per campaign')
      }
    },
    detailHandler = state => {
      navigate('/Answers', { state: state })
    },
    returnToCampaigns = () => {
      navigate('/')
    }

  return (
    <div>
      <PopUpModal
        deleteText='question'
        deleteShow={deleteShow}
        handleClose={handleClose}
        deleteObject={deleteObject}></PopUpModal>
      <AlertDismissable
        dismiss={() => setEditError('')}
        show={!!editError}
        message={editError}
        type='error'
      />
      <Button
        style={{
          fontWeight: 'bold',
          margin: '10px',
          float: 'left'
        }}
        variant='secondary'
        onClick={() => returnToCampaigns()}>
        Return to Campaigns
      </Button>
      {sessionUser && sessionUser.isAdmin && (
        <Button
          style={{
            fontWeight: 'bold',
            margin: '10px',
            float: 'right'
          }}
          variant='primary'
          onClick={() => createHandler()}>
          Create Question
        </Button>
      )}
      <QuestionForm
        show={show}
        setShow={toggleShow}
        isEdit={isEdit}
        id={selectedRow.id}
        campaignId={selectedRow.campaignId}
        question={selectedRow.question}
        isMultiple={selectedRow.isMultiple}
        isActive={selectedRow.isActive}
        sessionUser={sessionUser}
        brandMetricType={selectedRow.brandMetricType}
        answers={selectedRow.answers}
        loadQuestions={loadQuestions}
      />
      {campaignSummary && (
        <Card className="mx-2 mb-3" style={{ clear: 'both' }}>
          <Card.Body>
            <Card.Title>Campaign Overview</Card.Title>
            <Row>
              <Col md={2}>
                <div className="text-muted small">Total Impressions</div>
                <div className="fw-bold">{formatNumber(campaignSummary.totals?.impressions)}</div>
              </Col>
              <Col md={2}>
                <div className="text-muted small">Total Cost</div>
                <div className="fw-bold">{formatCurrency(campaignSummary.totals?.cost)}</div>
              </Col>
              <Col md={2}>
                <div className="text-muted small">Survey Responses</div>
                <div className="fw-bold">{formatNumber(campaignSummary.totals?.surveyResponses)}</div>
              </Col>
              <Col md={3}>
                <div className="text-muted small">Response Rate</div>
                <div className="fw-bold">
                  {formatRate(campaignSummary.responseRate?.overall)}
                  <span className="text-muted small ms-2">
                    (E: {formatRate(campaignSummary.responseRate?.exposed)} / C: {formatRate(campaignSummary.responseRate?.control)})
                  </span>
                </div>
              </Col>
              <Col md={1}>
                <div className="text-muted small">Views Today</div>
                <div className="fw-bold">{formatNumber(campaignSummary.totals?.impressionsToday)}</div>
              </Col>
              <Col md={2}>
                <div className="text-muted small">Views Yesterday</div>
                <div className="fw-bold">{formatNumber(campaignSummary.totals?.impressionsYesterday)}</div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}
      <Accordion defaultActiveKey={[]} className="mx-2 mb-3" style={{ clear: 'both' }}>
        <Accordion.Item eventKey="0">
          <Accordion.Header>Column Options</Accordion.Header>
          <Accordion.Body>
            <Form style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 24px' }}>
              {columns.filter((c) => c.showInColumnFilters).map((col) => (
                <Form.Check
                  key={col.id}
                  type="switch"
                  id={`q-col-${col.id}`}
                  label={col.label}
                  checked={questionColumns.includes(col.id)}
                  onChange={(e) => {
                    setQuestionColumns((prev) =>
                      e.target.checked
                        ? [...prev, col.id]
                        : prev.filter((c) => c !== col.id)
                    );
                  }}
                />
              ))}
            </Form>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
      <DataTable
        title={`${state ? state.campaignName : ''}'s Questions`}
        columns={columns}
        data={data}
        customStyles={customStyles}
        progressPending={pending}
      />
    </div>
  )
}

export default Question
